import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {validateEmail} from 'src/utils/index';
import {
  ERROR_CODES,
  getTranslatedError,
} from 'src/components/authentication/errors';

import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  TitleMedium,
  BodyLarge,
  SubTitleMedium,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import {
  forgotPassword,
  forgotPasswordNoAgency,
} from 'src/action-creators/authentication';

import {selectForgotPasswordError} from 'src/selectors/authentication';

import css from './forgot-password.css';
import maincss from 'src/components/authentication/main.css';

import type {ForgotPasswordFormProps} from 'src/types/authentication';


const ForgotPassword = (props: ForgotPasswordFormProps) => {
  const {t} = useLabelTranslation();
  const {dynamicAgency, showConfirmation} = props;

  const dispatch = useDispatch();

  const forgotPasswordError = useSelector(selectForgotPasswordError);
  const [email, setEmail] = React.useState<string>('');
  const [isValidEmail, setIsValidEmail] = React.useState<boolean>(true);
  const [isSendingEmail, setIsSendingEmail] = React.useState<boolean>(false);

  const validateUserEmail = () => {
    setIsValidEmail(validateEmail(email));
  };

  const handleReset = async () => {
    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    let response;

    setIsSendingEmail(true);
    if (dynamicAgency) {
      response = await dispatch(forgotPasswordNoAgency(email));
    } else {
      response = await dispatch(forgotPassword(email));
    }

    setIsSendingEmail(false);
    // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    if (response) {
      showConfirmation();
    }
  };

  const handleEmailChange = (value: string) => {
    if (!isValidEmail) {
      setIsValidEmail(true);
    }

    setEmail(value);
  };

  const {isExtension} = useEnvironmentContext();

  return (
    <div className={css.resetSection}>
      <div className={css.titleAndSubtitle}>
        {isExtension ? (
          <SubTitleMedium className={maincss.alignSelfCenter}>
            {getTranslatedText(t, CONSTANTS.RESET_PASSWORD_TITLE)}
          </SubTitleMedium>
        ) : (
          <TitleMedium>
            {getTranslatedText(t, CONSTANTS.RESET_PASSWORD_TITLE)}
          </TitleMedium>
        )}
        {isExtension ? (
          <BodySmall className={maincss.textAlignCenter}>
            {getTranslatedText(t, CONSTANTS.RESET_PASSWORD_SUBTEXT)}
          </BodySmall>
        ) : (
          <BodyLarge>
            {getTranslatedText(t, CONSTANTS.RESET_PASSWORD_SUBTEXT)}
          </BodyLarge>
        )}
      </div>
      <div>
        <Input
          label={t('Email', 'Email')}
          placeholder={t('Enter_email', 'Enter email')}
          size="medium"
          type="text"
          value={email}
          error={!isValidEmail}
          errorText={getTranslatedText(t, CONSTANTS.INVALID_EMAIL)}
          onBlur={validateUserEmail}
          onChange={(e) => handleEmailChange(e.target.value)}
        />
      </div>
      <Button
        size="medium"
        type="primary"
        onClick={handleReset}
        disabled={isSendingEmail}
      >
        {isSendingEmail
          ? t('Sending_email', 'Sending email')
          : t('Reset_password', 'Reset password')}
      </Button>

      {forgotPasswordError && (
        <InContextAlert
          semantic="danger"
          classNames={{wrapper: css.errorNotification}}
        >
          {getTranslatedError(
            t,
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
            ERROR_CODES[forgotPasswordError] || forgotPasswordError,
          )}
        </InContextAlert>
      )}
    </div>
  );
};

export default ForgotPassword;
